import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, LOGO_ICON, SITE_NAME } from "@/content/siteConfig";
import { GooglePlayBadge } from "@/components/brand/GooglePlayBadge";

export const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-canvas/90 backdrop-blur-md border-b border-cvborder" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 sm:py-5">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src={LOGO_ICON} alt={SITE_NAME} width={36} height={36} className="h-9 w-9 rounded-[10px]" />
          <span className="font-heading text-xl">{SITE_NAME}</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[15px] font-medium transition-colors ${
                  isActive ? "text-ink" : "text-cvbody hover:text-ink"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <GooglePlayBadge data-testid="header-play-cta" className="h-10" />
        </div>

        <button
          className="flex h-10 w-10 items-center justify-center rounded-full border border-cvborder lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-cvborder bg-canvas px-6 py-6 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-5">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base font-medium text-ink"
              >
                {link.label}
              </NavLink>
            ))}
            <GooglePlayBadge data-testid="mobile-header-play-cta" className="mt-2 h-11 justify-center" />
          </div>
        </div>
      )}
    </header>
  );
};
